/**
 * @file src/input/midi/slotQualifier.js
 * @description The stage-position qualifier on a PERSISTED parameter id.
 *
 * A collection-owned parameter binding belongs to a stage POSITION, so two stages must be able to
 * hold independent mappings for the same component inside the one collection slice. The position
 * therefore has to be part of the stored parameter id.
 *
 * It is carried as a PREFIX and never inside the layered key itself:
 *
 *     stored:   slot-2::layered:x.knob|deck-i|default
 *     runtime:            layered:x.knob|deck-i|default
 *
 * The layered key's middle segment is the AUDIO STACK (`deck-i`, and the not-yet-enabled
 * `deck-ii`/`send-i`/`main`), and it is read back out at runtime and handed to `setScopedState`,
 * which silently rejects any stack that is not enabled. Putting a stage position there would
 * therefore break scoped-state readback for every collection-owned control. Keeping it as a
 * prefix also leaves the runtime key byte-identical to the orbiter-owned one, so switching where
 * mappings are saved changes only WHICH slice is read — never how a binding is interpreted.
 *
 * Qualified ids exist only between this module and the backend; nothing downstream of
 * {@link stripSlotQualifier} ever sees one.
 */

const SEPARATOR = '::';
const PREFIX = 'slot-';

/**
 * @param {string} parameterId a bare (runtime) parameter id.
 * @param {number|null|undefined} slot 1-based stage position; null → returned unchanged.
 * @returns {string}
 */
export function qualifyForSlot(parameterId, slot) {
  if (!parameterId || !Number.isInteger(slot) || slot <= 0) {
    return parameterId;
  }
  return `${PREFIX}${slot}${SEPARATOR}${parameterId}`;
}

/**
 * The 1-based slot a stored id is qualified for, or null when it carries no qualifier.
 * @param {string} parameterId
 * @returns {number|null}
 */
export function slotOf(parameterId) {
  if (typeof parameterId !== 'string' || !parameterId.startsWith(PREFIX)) {
    return null;
  }
  const separator = parameterId.indexOf(SEPARATOR);
  if (separator <= PREFIX.length) {
    return null;
  }
  const slot = Number(parameterId.slice(PREFIX.length, separator));
  return Number.isInteger(slot) && slot > 0 ? slot : null;
}

/**
 * The runtime parameter id, with any slot qualifier removed. Safe on unqualified ids.
 * @param {string} parameterId
 * @returns {string}
 */
export function stripSlotQualifier(parameterId) {
  if (slotOf(parameterId) === null) {
    return parameterId;
  }
  return parameterId.slice(parameterId.indexOf(SEPARATOR) + SEPARATOR.length);
}

/**
 * One slice's bindings as the RUNTIME view for a single stage: only the entries qualified for
 * `slot`, re-keyed to their bare parameter ids.
 *
 * Filtering by position is what stops one stage hydrating another's binding: hydration matches
 * candidates on the component key alone, and inbound dispatch fires EVERY channel/CC match a
 * widget holds — so an unfiltered shared slice would let stage 2's CC drive stage 1.
 *
 * @param {Map<string, {channel:number, cc:number}>|null} bindings the raw slice.
 * @param {number|null} slot 1-based stage position.
 * @returns {Map<string, {channel:number, cc:number}>|null}
 */
export function bindingsForSlot(bindings, slot) {
  if (!bindings || !Number.isInteger(slot) || slot <= 0) {
    return null;
  }
  const view = new Map();
  bindings.forEach((binding, key) => {
    if (slotOf(key) === slot) {
      view.set(stripSlotQualifier(key), binding);
    }
  });
  return view;
}
